import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  DollarSign,
  TrendingUp,
  FolderKanban,
  Users,
  FileText,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
  Trash2,
  ChevronDown,
  ChevronUp,
  Calendar,
  User,
  Hash,
  CreditCard
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';

import { useTranslation } from 'react-i18next';
import AuditStreamWidget from '../components/AuditStreamWidget';

const Dashboard = ({ user }) => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [financialData, setFinancialData] = useState(null);
  const [expandedActivity, setExpandedActivity] = useState(null);
  const [activityDetails, setActivityDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [userPages, setUserPages] = useState([]);
  
  // Check if current user is admin
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchData();
    // Get user permissions from localStorage
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setUserPages(user.pages || []);
    
    // Auto-refresh every 60 seconds
    const interval = setInterval(() => {
      fetchData();
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const results = await Promise.allSettled([
        axiosInstance.get('/dashboard/stats'),
        axiosInstance.get('/reports/financial')
      ]);
      const failed = [];
      if (results[0].status === 'fulfilled') setStats(results[0].value.data);
      else { failed.push('stats'); console.error('Failed /dashboard/stats:', results[0].reason?.response?.status, results[0].reason?.message); }
      if (results[1].status === 'fulfilled') setFinancialData(results[1].value.data);
      else { failed.push('reports'); console.error('Failed /reports/financial:', results[1].reason?.response?.status, results[1].reason?.message); }
      if (failed.length && loading) {
        toast.error(`Failed to load: ${failed.join(', ')}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const deleteActivity = async (e, activityId, activityType) => {
    e.stopPropagation(); // Prevent expanding the activity
    if (!window.confirm(t('dashboard.are_you_sure_you_want_to_delete_this_act'))) return;
    
    try {
      if (activityType === 'invoice') {
        await axiosInstance.delete(`/invoices/${activityId}`);
        toast.success(t('dashboard.invoice_deleted_successfully'));
      } else if (activityType === 'quotation') {
        await axiosInstance.delete(`/quotations/${activityId}`);
        toast.success(t('dashboard.quotation_deleted_successfully'));
      } else if (activityType === 'payment') {
        await axiosInstance.delete(`/payments/${activityId}`);
        toast.success(t('dashboard.payment_deleted_successfully'));
      } else if (activityType === 'expense') {
        await axiosInstance.delete(`/expenses/${activityId}`);
        toast.success(t('dashboard.expense_deleted_successfully'));
      } else if (activityType === 'project') {
        await axiosInstance.delete(`/projects/${activityId}`);
        toast.success(t('dashboard.project_deleted_successfully'));
      }
      setExpandedActivity(null);
      setActivityDetails(null);
      fetchData(); // Refresh data
    } catch (error) {
      const errMsg = error.response?.data?.detail || 'Failed to delete activity';
      toast.error(errMsg);
    }
  };

  const toggleActivityDetails = async (activity) => {
    if (expandedActivity === activity.id) {
      setExpandedActivity(null);
      setActivityDetails(null);
      return;
    }
    
    setExpandedActivity(activity.id);
    setLoadingDetails(true);
    
    try {
      if (activity.type === 'invoice') {
        const response = await axiosInstance.get(`/invoices/${activity.id}`);
        setActivityDetails(response.data);
      }
    } catch (error) {
      toast.error('Failed to fetch details');
    } finally {
      setLoadingDetails(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Helper function to check if user has permission for a page
  const hasPermission = (page) => {
    // Admin and users with empty pages array have access to all
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.role === 'admin') return true;
    if (!userPages || userPages.length === 0) return true;
    return userPages.includes(page);
  };

  const allStatCards = [
    {
      title: 'Total Revenue',
      value: `${stats?.total_revenue?.toLocaleString() || 0} SAR`,
      icon: DollarSign,
      color: 'green',
      change: '+12.5%',
      up: true,
      requiredPage: 'reports'
    },
    {
      title: 'Active Projects',
      value: stats?.active_projects || 0,
      icon: FolderKanban,
      color: 'blue',
      change: `${stats?.total_projects || 0} total`,
      up: true,
      requiredPage: 'projects'
    },
    {
      title: 'Pending Invoices',
      value: `${stats?.pending_invoices?.toLocaleString() || 0} SAR`,
      icon: FileText,
      color: 'orange',
      change: 'Awaiting payment',
      up: false,
      requiredPage: 'invoices'
    },
    {
      title: 'Total Expenses',
      value: `${stats?.total_expenses?.toLocaleString() || 0} SAR`,
      icon: TrendingUp,
      color: 'red',
      change: 'This month',
      up: false,
      requiredPage: 'expenses'
    },
    {
      title: 'Employees',
      value: stats?.employees_count || 0,
      icon: Users,
      color: 'purple',
      change: 'Active staff',
      up: true,
      requiredPage: 'employees'
    },
    {
      title: 'Customers',
      value: stats?.customers_count || 0,
      icon: Users,
      color: 'teal',
      change: 'Total clients',
      up: true,
      requiredPage: 'customers'
    },
    {
      title: 'Net Profit',
      value: `${financialData?.net_profit?.toLocaleString() || 0} SAR`,
      icon: DollarSign,
      color: 'green',
      change: `${financialData?.profit_margin?.toFixed(1) || 0}% margin`,
      up: true,
      requiredPage: 'reports'
    },
  ];

  // Filter stat cards based on user permissions
  const statCards = allStatCards.filter(card => hasPermission(card.requiredPage));

  const monthlyData = Object.keys(financialData?.income_by_month || {}).map(month => ({
    month,
    income: financialData?.income_by_month[month] || 0,
    expenses: financialData?.expense_by_month[month] || 0,
    profit: (financialData?.income_by_month[month] || 0) - (financialData?.expense_by_month[month] || 0)
  })).sort((a, b) => a.month.localeCompare(b.month));

  const COLORS = ['#667eea', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6'];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              data-testid={`stat-card-${stat.title.toLowerCase().replace(/ /g, '-')}`}
              className="stat-card"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`stat-icon ${stat.color}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <span className={`flex items-center text-sm font-semibold ${
                  stat.up ? 'text-green-600' : 'text-gray-500'
                }`}>
                  {stat.up ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                  {stat.change}
                </span>
              </div>
              <h3 className="text-gray-600 text-sm font-medium mb-1">{stat.title}</h3>
              <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
            </div>
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue vs Expenses Chart */}
        <div className="chart-container">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Revenue vs Expenses</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px'
                }} 
              />
              <Legend />
              <Bar dataKey="income" fill="#10b981" name="Income" radius={[8, 8, 0, 0]} />
              <Bar dataKey="expenses" fill="#ef4444" name="Expenses" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Profit Trend */}
        <div className="chart-container">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Profit Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px'
                }} 
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="profit" 
                stroke="#667eea" 
                strokeWidth={3}
                name="Net Profit"
                dot={{ fill: '#667eea', r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Audit Stream Widget — Control Plane Observability (Phase 7.x · P0.5) */}
      <AuditStreamWidget />

      {/* Recent Activities */}
      <div className="table-container">
        <div className="p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Recent Activities</h3>
          {stats?.recent_activities && stats.recent_activities.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_activities.map((activity, index) => (
                <div key={activity.id || index}>
                  <div
                    data-testid={`recent-activity-${index}`}
                    className={`flex items-center justify-between p-4 rounded-xl transition-colors cursor-pointer ${
                      expandedActivity === activity.id ? 'bg-purple-50 border-2 border-purple-200' : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                    onClick={() => toggleActivityDetails(activity)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{activity.description}</p>
                        <p className="text-sm text-gray-500">{new Date(activity.date).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-bold text-gray-900">{activity.amount?.toLocaleString()} SAR</p>
                        <span className={`status-badge ${activity.status}`}>{activity.status}</span>
                      </div>
                      {/* Delete button - Admin only */}
                      {isAdmin && (
                        <button
                          onClick={(e) => deleteActivity(e, activity.id, activity.type)}
                          className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                          title={t('dashboard.delete')}
                          data-testid={`delete-activity-${index}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                      {expandedActivity === activity.id ? (
                        <ChevronUp className="w-5 h-5 text-purple-600" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                  
                  {/* Expanded Details Panel */}
                  {expandedActivity === activity.id && (
                    <div className="mt-2 p-4 bg-white border-2 border-purple-100 rounded-xl shadow-sm animate-in slide-in-from-top-2">
                      {loadingDetails ? (
                        <div className="text-center py-4">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                          <p className="text-gray-500 mt-2">Loading details...</p>
                        </div>
                      ) : activityDetails ? (
                        <div className="grid grid-cols-4 gap-4">
                          {/* Invoice Info */}
                          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-4 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <Hash className="w-4 h-4 text-purple-600" />
                              <span className="text-sm text-gray-500">Invoice Number</span>
                            </div>
                            <p className="font-bold text-purple-700">{activityDetails.invoice_number}</p>
                          </div>
                          
                          {/* Client Info */}
                          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <User className="w-4 h-4 text-blue-600" />
                              <span className="text-sm text-gray-500">Client</span>
                            </div>
                            <p className="font-bold text-blue-700">{activityDetails.client_name}</p>
                            <p className="text-xs text-gray-500">{activityDetails.client_email}</p>
                          </div>
                          
                          {/* Date Info */}
                          <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <Calendar className="w-4 h-4 text-green-600" />
                              <span className="text-sm text-gray-500">Issue Date</span>
                            </div>
                            <p className="font-bold text-green-700">{activityDetails.issue_date}</p>
                            <p className="text-xs text-gray-500">Due: {activityDetails.due_date}</p>
                          </div>
                          
                          {/* Amount Info */}
                          <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-4 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <CreditCard className="w-4 h-4 text-orange-600" />
                              <span className="text-sm text-gray-500">Total Amount</span>
                            </div>
                            <p className="font-bold text-orange-700">{activityDetails.total?.toLocaleString()} SAR</p>
                            <p className="text-xs text-gray-500">VAT: {activityDetails.vat_amount?.toLocaleString()} SAR</p>
                          </div>
                          
                          {/* Items Table */}
                          {activityDetails.items && activityDetails.items.length > 0 && (
                            <div className="col-span-4 mt-2">
                              <p className="text-sm font-semibold text-gray-700 mb-2">Invoice Items</p>
                              <div className="bg-gray-50 rounded-lg overflow-hidden">
                                <table className="w-full text-sm">
                                  <thead className="bg-gray-100">
                                    <tr>
                                      <th className="px-3 py-2 text-left">Description</th>
                                      <th className="px-3 py-2 text-right">Qty</th>
                                      <th className="px-3 py-2 text-right">Unit Price</th>
                                      <th className="px-3 py-2 text-right">Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {activityDetails.items.map((item, idx) => (
                                      <tr key={idx} className="border-t border-gray-200">
                                        <td className="px-3 py-2">{item.description}</td>
                                        <td className="px-3 py-2 text-right">{item.quantity}</td>
                                        <td className="px-3 py-2 text-right">{item.unit_price?.toLocaleString()} SAR</td>
                                        <td className="px-3 py-2 text-right font-semibold">{item.total?.toLocaleString()} SAR</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                          
                          {/* Notes */}
                          {activityDetails.notes && (
                            <div className="col-span-4 bg-yellow-50 p-3 rounded-lg">
                              <p className="text-sm font-semibold text-yellow-700">Notes:</p>
                              <p className="text-sm text-gray-600">{activityDetails.notes}</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-center text-gray-500">No details available</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <AlertCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No recent activities</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
